import SlideModal from 'components/SlideModal';
import { IOption } from 'entities';
import useClickOutside from 'hooks/useClickOutside';
import { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { useMediaQuery } from 'react-responsive';
import { MAX_WIDTH_MOBILE } from 'utils/constant';
import { ReactComponent as IconClose } from '../../assets/close.svg';
import { ReactComponent as IconDropdownArrow } from '../../assets/dropdown_arrow.svg';
import IconInfo from '../../assets/icon_info.png';
import { ReactComponent as IconSearch } from '../../assets/search.svg';
import styles from './Dropdown.module.scss';

interface DropdownProps {
  type?: 'original' | 'blocks';
  placeholder?: string;
  button?: (active: boolean) => JSX.Element;
  options: IOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  style?: CSSProperties;
  clearable?: boolean;
  infoOnClick?: (value: string) => void;
  readOnly?: boolean;
}

const Dropdown: FC<DropdownProps> = ({
  type = 'original',
  placeholder,
  button,
  options,
  value,
  onChange,
  style,
  clearable,
  infoOnClick,
  readOnly,
}) => {
  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });

  const [isExpanded, setIsExpanded] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [buttonHeight, setButtonHeight] = useState<number>();
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

  useClickOutside([buttonRef, contentRef], () => setIsExpanded(false));

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    const observer = new ResizeObserver(([entry]) => entry && setButtonHeight(entry.contentRect.height));

    observer.observe(button);

    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.container} style={{ maxHeight: buttonHeight, ...style }}>
      <div
        {...(readOnly
          ? {}
          : getToggleProps({
              onClick: () => setIsExpanded((prev) => !prev),
            }))}
        aria-hidden="false"
        ref={buttonRef}
        className={`${styles.button} ${readOnly && styles.readOnly}`}
      >
        {button?.(isExpanded) ?? (
          <button
            className={`${styles.dropdown} ${isExpanded && styles.isExpanded} ${type === 'blocks' && styles.blocks} ${
              type === 'blocks' && options.find((opt) => opt.value === value) && styles.blocksSelected
            }`}
          >
            {type === 'blocks' && !options.find((opt) => opt.value === value) && (
              <div className={styles.searchIcon}>
                <IconSearch />
              </div>
            )}
            <span className={`${!value && styles.placeholder} ${type === 'blocks' && styles.blocksLabel}`}>
              {options.find((opt) => opt.value === value)?.name || placeholder}
            </span>
            {clearable && options.find((opt) => opt.value === value) ? (
              <div
                className={styles.clearIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                  setIsExpanded(false);
                }}
              >
                <IconClose />
              </div>
            ) : (
              type === 'original' && <IconDropdownArrow className={styles.arrow} />
            )}
          </button>
        )}
      </div>

      {isMobile ? (
        <SlideModal isOpen={isExpanded} onClose={() => setIsExpanded(false)}>
          <div className={styles.slideModal}>
            <div className={styles.options}>
              {options.map((opt) => (
                <div
                  key={opt.value}
                  className={`${styles.option} ${opt.value === value ? styles.active : ''}`}
                  onClick={() => {
                    onChange(opt.value);
                    setIsExpanded(false);
                  }}
                >
                  {opt.name}

                  {infoOnClick && opt.value !== 'All' && (
                    <img
                      src={IconInfo}
                      width={25}
                      height={25}
                      alt="info"
                      onClick={(e) => {
                        e.stopPropagation();
                        infoOnClick(opt.value);
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </SlideModal>
      ) : type === 'original' ? (
        <div {...getCollapseProps()} className={styles.contentContainer}>
          <div className={`${styles.content}`} ref={contentRef}>
            {options.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.option} ${opt.value === value ? styles.active : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setIsExpanded(false);
                }}
              >
                {opt.name}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div {...getCollapseProps()} className={styles.blocksContentContainer}>
          <div className={`${styles.blocksContent}`} ref={contentRef}>
            {options.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.option} ${opt.value === value && styles.selected}`}
                onClick={() => {
                  onChange(opt.value);
                  setIsExpanded(false);
                }}
              >
                {opt.name}

                {infoOnClick && opt.value !== 'All' && (
                  <img
                    src={IconInfo}
                    width={20}
                    height={20}
                    alt="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      infoOnClick(opt.value);
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
